import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientCustomerType, LeadCloudFileType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import * as apiClient from 'api/apiClients';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import { parseCloudFiles } from 'modules/files';
import { useObjectCard } from '../../context/ObjectCardProvider';
import {
    EMPTY_CUSTOMER,
    normalizeCustomerPayload,
    parseObjectCustomer,
} from './customerFormUtils';
import useCustomerFormFields from './useCustomerFormFields';

const useObjectTabCustomer = () => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { vObject, fetchObject, setShowSpinner, isEditLocked } = useObjectCard();
    const [catalog, setCatalog] = useState<ClientCustomerType[]>([]);
    const [customerFiles, setCustomerFiles] = useState<LeadCloudFileType[]>([]);
    const filesOwnerIdRef = useRef('');

    const clientId = Number(vObject?.object?.clientId || 0) || 0;
    const objectCustomerRaw = vObject?.object?.objectCustomer;
    const initialCustomer = useMemo(
        () => parseObjectCustomer(objectCustomerRaw),
        [objectCustomerRaw],
    );
    const resetKey =
        typeof objectCustomerRaw === 'string'
            ? objectCustomerRaw
            : JSON.stringify(objectCustomerRaw || '');

    const fields = useCustomerFormFields({
        initialCustomer,
        resetKey: `${vObject?.object?.ID || 0}:${resetKey}`,
        readOnly: Boolean(isEditLocked),
    });

    const loadCatalog = async () => {
        if (!clientId) {
            setCatalog([]);
            return;
        }
        const list = await apiClient.getClientCustomers(clientId);
        setCatalog(Array.isArray(list) ? list : []);
    };

    useEffect(() => {
        void loadCatalog();
    }, [clientId]);

    const formCode = String(fields.formValues.edrpouOrIpn || '').trim();
    const selectedCatalogId = useMemo(() => {
        if (!formCode) return '';
        const row = catalog.find(
            (item) => String(item.edrpouOrIpn || item.customer?.edrpouOrIpn || '').trim() === formCode,
        );
        return row ? String(row.ID) : '';
    }, [catalog, formCode]);

    useEffect(() => {
        if (!clientId || !selectedCatalogId) {
            setCustomerFiles([]);
            filesOwnerIdRef.current = '';
            return;
        }
        if (filesOwnerIdRef.current === selectedCatalogId) return;

        let cancelled = false;
        void (async () => {
            const row = await apiClient.getClientCustomer(clientId, selectedCatalogId);
            if (cancelled) return;
            filesOwnerIdRef.current = selectedCatalogId;
            setCustomerFiles(parseCloudFiles(row?.files));
        })();

        return () => {
            cancelled = true;
        };
    }, [clientId, selectedCatalogId]);

    const handleSelectCatalog = (customerId: string) => {
        if (!customerId) return;
        const row = catalog.find((item) => String(item.ID) === customerId);
        if (!row) return;
        filesOwnerIdRef.current = '';
        fields.setFormValues({
            ...EMPTY_CUSTOMER,
            ...parseObjectCustomer(row.customer),
            fullName: row.fullName || row.customer?.fullName || '',
            edrpouOrIpn: row.edrpouOrIpn || row.customer?.edrpouOrIpn || '',
        });
    };

    const persistCustomer = async (): Promise<{ stat: boolean; clientCustomerId?: number }> => {
        if (!vObject?.object?.ID) return { stat: false };
        const payload = normalizeCustomerPayload(fields.formValues);
        const res = await apiObject.setCustomer(vObject.object.ID, payload);
        await fetchObject();
        await loadCatalog();
        return res;
    };

    const handleSave = async () => {
        if (!vObject?.object?.ID || fields.readOnly || fields.isSaving) return;

        fields.setIsSaving(true);
        setShowSpinner(true);
        const res = await persistCustomer();

        if (res.stat) showToast(t('messCustomerSaved'), 'success');
        else showToast(t('common:messError'), 'danger');

        fields.setIsSaving(false);
        setShowSpinner(false);
    };

    const handleEnsureCustomerForFiles = async (): Promise<CloudUploadScope | false> => {
        const payload = normalizeCustomerPayload(fields.formValues);
        if (!payload.edrpouOrIpn) {
            showToast(t('messSaveCustomerFirst'), 'warning');
            return false;
        }

        fields.setIsSaving(true);
        setShowSpinner(true);
        const res = await persistCustomer();
        fields.setIsSaving(false);
        setShowSpinner(false);

        if (!res.stat) {
            showToast(t('common:messError'), 'danger');
            return false;
        }

        const clientCustomerId = Number(res.clientCustomerId || 0);
        if (!clientCustomerId) {
            showToast(t('messSaveCustomerFirst'), 'warning');
            return false;
        }

        filesOwnerIdRef.current = String(clientCustomerId);
        return { clientCustomerId };
    };

    const handleCustomerFilesChange = async (nextFiles: LeadCloudFileType[]): Promise<boolean> => {
        setCustomerFiles(nextFiles);
        return true;
    };

    const showCatalogSelect = clientId > 0 && !fields.readOnly;

    return {
        ...fields,
        catalog,
        selectedCatalogId,
        showCatalogSelect,
        handleSelectCatalog,
        handleSave,
        customerFiles,
        clientCustomerId: Number(selectedCatalogId || 0) || 0,
        handleCustomerFilesChange,
        handleEnsureCustomerForFiles,
    };
};

export default useObjectTabCustomer;
